/**
 * Invoice Notes List Component
 * Displays all notes for an invoice with create/edit/delete functionality
 */

import React, { useState, useEffect } from 'react';
import { Card, Button, Spinner, Alert } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { invoiceNotesApi } from '../../../../../services/api/invoiceNotesApi';
import InvoiceNoteCard from './InvoiceNoteCard';
import CreateNoteButton from './CreateNoteButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStickyNote } from '@fortawesome/free-solid-svg-icons';

const InvoiceNotesList = ({ invoiceId: propInvoiceId }) => {
  const { companyId, invoiceId: paramInvoiceId } = useParams();
  const invoiceId = propInvoiceId || paramInvoiceId;

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch notes on mount and when invoiceId changes
  useEffect(() => {
    if (invoiceId && companyId) {
      fetchNotes();
    }
  }, [invoiceId, companyId]);

  // Fetch all notes for the invoice
  const fetchNotes = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await invoiceNotesApi.getAll(companyId, invoiceId);

      if (response.data && response.data.success) {
        setNotes(response.data.data || []);
      } else {
        setNotes([]);
      }
    } catch (err) {
      console.error('Error fetching notes:', err);
      setError('Kunde inte ladda anteckningar');
    } finally {
      setLoading(false);
    }
  };

  // Handle note created
  const handleNoteCreated = () => {
    fetchNotes();
  };

  // Handle note updated
  const handleNoteUpdated = () => {
    fetchNotes();
  };

  // Handle note deleted
  const handleNoteDeleted = () => {
    fetchNotes();
  };

  if (loading) {
    return (
      <Card>
        <Card.Header>
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faStickyNote} className="me-2" />
            Anteckningar
          </h5>
        </Card.Header>
        <Card.Body className="text-center py-4">
          <Spinner animation="border" size="sm" />
          <p className="mt-2 mb-0">Laddar anteckningar...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Card.Header>
          <h5 className="mb-0">
            <FontAwesomeIcon icon={faStickyNote} className="me-2" />
            Anteckningar
          </h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="danger">{error}</Alert>
          <Button variant="primary" size="sm" onClick={fetchNotes}>
            Försök igen
          </Button>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          <FontAwesomeIcon icon={faStickyNote} className="me-2" />
          Anteckningar {notes.length > 0 && `(${notes.length})`}
        </h5>
        <CreateNoteButton
          companyId={companyId}
          invoiceId={invoiceId}
          onNoteCreated={handleNoteCreated}
        />
      </Card.Header>
      <Card.Body>
        {notes.length === 0 ? (
          <div className="text-center text-muted py-4">
            <FontAwesomeIcon icon={faStickyNote} size="3x" className="mb-3 opacity-25" />
            <p className="mb-0">Inga anteckningar ännu.</p>
            <p className="small">Klicka på knappen ovan för att lägga till en.</p>
          </div>
        ) : (
          <div className="notes-list">
            {notes.map((note, index) => (
              <InvoiceNoteCard
                key={note.id}
                note={note}
                companyId={companyId}
                invoiceId={invoiceId}
                onUpdate={handleNoteUpdated}
                onDelete={handleNoteDeleted}
                isLast={index === notes.length - 1}
              />
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default InvoiceNotesList;
